import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

import { SAMPLE_CARDS } from '../../samples/sampleCards';
import { useImpression } from '../../hooks/useImpression';
import { useIsPhone } from '../../hooks/useIsPhone';
import { captureFunnelEvent, trackButtonClick } from '../../services/telemetry';

/**
 * Shared home-page sections. The phone landing
 * (`HomeMobileLanding`) and the desktop page (`Home`) render the same markup
 * so the two breakpoints tell one story; only the CTA action differs (camera
 * capture on phones, scroll-to-upload-box on desktop).
 */

export function HomeCtaButton({
  label,
  icon,
  onClick,
}: {
  label: string;
  icon: IconDefinition;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`
        flex w-full cursor-pointer items-center justify-center gap-3
        rounded-full bg-yellow-400 px-5 py-[18px] text-[19px] font-bold
        text-gray-800 shadow-[0_4px_0_#eab308] transition-[transform,background]
        duration-100
        hover:bg-yellow-500
        active:scale-[0.985]
        md:py-5 md:text-xl
        lg:gap-4 lg:py-6 lg:text-2xl lg:shadow-[0_5px_0_#eab308]
      `}
    >
      <FontAwesomeIcon
        icon={icon}
        aria-hidden="true"
        className="text-xl lg:text-2xl"
      />
      {label}
    </button>
  );
}

function HowStep({
  n,
  title,
  detail,
}: {
  n: number;
  title: string;
  detail: string;
}) {
  return (
    <div
      className={`
        flex items-start gap-3.5 rounded-[14px] border border-gray-200 bg-white
        px-4 py-3.5
        md:gap-4 md:rounded-2xl md:px-5 md:py-4
        lg:gap-5 lg:px-7 lg:py-6
      `}
    >
      <span
        className={`
          flex h-7 w-7 shrink-0 items-center justify-center rounded-full
          bg-yellow-400 text-sm font-bold text-gray-800
          md:h-9 md:w-9 md:text-base
          lg:h-11 lg:w-11 lg:text-xl
        `}
      >
        {n}
      </span>
      <div>
        <div
          className={`
            mt-0.5 mb-0.5 text-[15px] font-semibold text-gray-800
            md:text-lg
            lg:mb-1 lg:text-xl
          `}
        >
          {title}
        </div>
        <div className={`text-sm text-gray-500 md:text-base lg:text-lg`}>
          {detail}
        </div>
      </div>
    </div>
  );
}

const DEFAULT_HOW_STEPS = [
  {
    title: 'Snap or upload',
    detail: 'Photograph a page of sheet music, or drop in a PDF / MusicXML.',
  },
  {
    title: 'AI adds the fingerings',
    detail: 'Trained on fingerings labeled by professional pianists.',
  },
  {
    title: 'Play, edit, or download',
    detail:
      'Adjust any fingering, toggle pitch names or accidental highlighting, then save or print your score.',
  },
] as const;

export function HowItWorksSection({
  className = '',
  steps = DEFAULT_HOW_STEPS,
}: {
  className?: string;
  steps?: readonly { title: string; detail: string }[];
}) {
  return (
    <section className={className}>
      <h2
        className={`
          mb-5 text-center text-xl font-bold text-gray-800
          md:mb-7 md:text-3xl
          lg:mb-9 lg:text-4xl
        `}
      >
        How it works
      </h2>
      <div className="flex flex-col gap-3 md:gap-4 lg:gap-5">
        {steps.map((step, i) => (
          <HowStep
            key={step.title}
            n={i + 1}
            title={step.title}
            detail={step.detail}
          />
        ))}
      </div>
    </section>
  );
}

export type SampleSurface = 'phone' | 'desktop';

// One impression per page load; both landings mount a copy.
let samplesViewedThisPage = false;

/** Homepage cards that open `/sample/:slug`. */
export function SamplesSection({
  className = '',
  surface,
  from,
}: {
  className?: string;
  surface: SampleSurface;
  /** When set, sample pages send Home / upload CTAs back here. */
  from?: string;
}) {
  const { ready } = useIsPhone();
  const sectionRef = useImpression<HTMLElement>(
    () => {
      if (samplesViewedThisPage) return;
      samplesViewedThisPage = true;
      captureFunnelEvent('home_samples_viewed', {
        count: SAMPLE_CARDS.length,
        surface,
      });
    },
    { enabled: ready }
  );

  return (
    <section ref={sectionRef} className={className}>
      <h2
        className={`
          mb-3.5 text-center text-lg font-bold text-gray-800
          md:mb-6 md:text-3xl
          lg:mb-8 lg:text-4xl
        `}
      >
        No sheet music handy?
      </h2>
      <div className="grid grid-cols-2 gap-3 md:gap-5 lg:gap-6">
        {SAMPLE_CARDS.map((sample) => (
          <Link
            key={sample.slug}
            to={`/sample/${sample.slug}`}
            state={from ? { from } : undefined}
            onClick={() => {
              void trackButtonClick(
                'Home',
                `Open sample: ${sample.cardTitle}`,
                {
                  slug: sample.slug,
                  surface,
                }
              );
              captureFunnelEvent('home_sample_opened', {
                slug: sample.slug,
                surface,
              });
            }}
            className={`
              group flex cursor-pointer flex-col rounded-[14px] border-[1.5px]
              border-gray-200 bg-white p-3 text-left shadow-md
              transition-[transform,box-shadow,border-color] duration-150
              hover:-translate-y-0.5 hover:border-yellow-400 hover:shadow-lg
              active:translate-y-0 active:scale-[0.985]
              md:rounded-2xl md:p-4
              lg:p-5
            `}
          >
            <div
              className={`
                mb-2 h-16 overflow-hidden rounded-lg bg-[#FBFAF5]
                md:mb-3 md:h-24
                lg:h-32
              `}
            >
              <img
                src={sample.cardSrc}
                alt={`${sample.cardTitle} by ${sample.composer}`}
                className="h-full w-full object-cover object-left"
              />
            </div>
            <span
              className={`
                block truncate text-sm font-semibold text-gray-800
                md:text-base
                lg:text-lg
              `}
            >
              {sample.cardTitle}
            </span>
            <span className={`block truncate text-xs text-gray-500 md:text-sm`}>
              {sample.composer}
            </span>
            {/* The card is the tap target; this pill is what makes it read as
                one. Hover is the only affordance phones never get, so the
                clickable cue has to be visible at rest. */}
            <span
              className={`
                mt-2.5 flex items-center justify-center rounded-full
                border-[1.5px] border-gray-300 bg-transparent px-3 py-1.5
                text-xs font-semibold text-gray-800 transition-colors
                duration-100
                group-hover:border-yellow-400 group-hover:bg-yellow-50
                md:mt-3 md:py-2 md:text-sm
                lg:text-base
              `}
            >
              Open sample
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}

export function FinalCtaSection({
  label,
  icon,
  onClick,
  className = '',
  heading = 'Stop guessing fingerings.',
  subheading = 'Try it on your own sheet music for free.',
}: {
  label: string;
  icon: IconDefinition;
  onClick: () => void;
  className?: string;
  heading?: string;
  subheading?: string;
}) {
  return (
    <section
      className={`
        rounded-[20px] bg-gray-800 px-[22px] py-[30px] text-center
        md:rounded-3xl md:px-10 md:py-12
        lg:px-14 lg:py-16
        ${className}
      `}
    >
      <h2
        className={`
          mb-1.5 text-[22px] font-bold text-[#FBFAF5]
          md:mb-2.5 md:text-3xl
          lg:mb-3 lg:text-4xl
        `}
      >
        {heading}
      </h2>
      <p
        className={`
          mb-5 text-[15px] text-[#C9C7BA]
          md:mb-7 md:text-xl
          lg:mb-9 lg:text-2xl
        `}
      >
        {subheading}
      </p>
      <div className="mx-auto max-w-sm md:max-w-md lg:max-w-lg">
        <HomeCtaButton label={label} icon={icon} onClick={onClick} />
      </div>
    </section>
  );
}
